import { useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  GraduationCap,
  GamepadIcon,
  Trophy,
  Users,
  DollarSign,
  PiggyBank,
  LineChart
} from "lucide-react";
import { useState, useEffect } from "react";

const BottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mode, setMode] = useState<'learn' | 'invest'>('learn');
  const [isVisible, setIsVisible] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Listen for mode changes using a custom event
  useEffect(() => {
    const handleModeChange = (e: CustomEvent<'learn' | 'invest'>) => {
      console.log("BottomNav - Custom event received:", e.detail);
      setIsTransitioning(true);
      // Add a small delay before changing the mode to allow for exit animations
      setTimeout(() => {
        setMode(e.detail);
        // Reset transitioning state after mode change
        setTimeout(() => setIsTransitioning(false), 50);
      }, 150);
    };

    // Add event listener for custom mode change event
    window.addEventListener('navigationModeChange', handleModeChange as EventListener);

    // Initial check of localStorage
    const savedMode = localStorage.getItem('navigationMode') as 'learn' | 'invest' | null;
    if (savedMode) {
      console.log("BottomNav - Initial mode from storage:", savedMode);
      setMode(savedMode);
    }

    return () => {
      window.removeEventListener('navigationModeChange', handleModeChange as EventListener);
    };
  }, []);

  // Updated useEffect to handle route changes and mode sync
  useEffect(() => {
    // Check if we're on an invest-related route
    const investPaths = ['/invest', '/save', '/track'];
    const isInvestRoute = investPaths.some(path => location.pathname === path);
    
    // Check if we're in lesson mode
    const isLessonMode = document.querySelector('[data-lesson-mode="true"]') !== null;
    setIsVisible(!isLessonMode);
    
    // Get mode from localStorage
    const savedMode = localStorage.getItem('navigationMode') as 'learn' | 'invest' | null;
    
    // Determine the new mode
    let newMode: 'learn' | 'invest';
    
    if (savedMode) {
      newMode = savedMode;
      console.log("BottomNav - Using saved mode:", savedMode);
    } else if (isInvestRoute) {
      newMode = 'invest';
      console.log("BottomNav - Using invest mode based on route");
    } else {
      newMode = 'learn';
      console.log("BottomNav - Using learn mode based on route");
    }
    
    console.log("BottomNav - Current mode:", mode);
    console.log("BottomNav - New mode:", newMode);
    console.log("BottomNav - Current path:", location.pathname);
    
    if (mode !== newMode) {
      console.log("BottomNav - Switching mode from", mode, "to", newMode);
      setIsTransitioning(true);
      setTimeout(() => {
        setMode(newMode);
        setTimeout(() => setIsTransitioning(false), 50);
      }, 150);
    }
  }, [location.pathname, mode]);

  const learnNavItems = [
    { href: "/home", label: "Dashboard", icon: LayoutDashboard },
    { href: "/learn", label: "Learn", icon: GraduationCap },
    { href: "/play", label: "Play", icon: GamepadIcon },
    { href: "/leaderboards", label: "Leaderboards", icon: Trophy },
    { href: "/community", label: "Community", icon: Users },
  ];

  const investNavItems = [
    { href: "/home", label: "Dashboard", icon: LayoutDashboard },
    { href: "/invest", label: "Invest", icon: DollarSign },
    { href: "/save", label: "Save", icon: PiggyBank },
    { href: "/track", label: "Track", icon: LineChart },
    { href: "/community", label: "Community", icon: Users },
  ];

  // Use the current mode to determine which nav items to show
  const navItems = mode === 'invest' ? investNavItems : learnNavItems;

  if (!isVisible) return null;

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-background/80 backdrop-blur-lg border-t md:hidden z-50 safe-area-bottom">
      <div className="flex items-center justify-around h-16 max-h-[4rem]">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.href;
          
          return (
            <button
              key={item.href}
              onClick={() => {
                console.log("BottomNav - Navigating to:", item.href);
                navigate(item.href);
              }}
              className={cn(
                "flex flex-col items-center justify-center w-full h-full space-y-1 px-1",
                "transition-all duration-300 ease-in-out",
                "active:scale-95 touch-manipulation",
                "hover:bg-gray-50/50",
                isTransitioning && "opacity-0 transform translate-y-2",
                !isTransitioning && "opacity-100 transform translate-y-0"
              )}
              style={{
                transitionDelay: `${parseInt(item.href.slice(-1)) * 50}ms`
              }}
            >
              <Icon
                className={cn(
                  "w-[18px] h-[18px] sm:w-5 sm:h-5",
                  "transition-all duration-300 ease-in-out",
                  isActive 
                    ? mode === 'learn' 
                      ? "text-primary scale-110" 
                      : "text-secondary scale-110"
                    : "text-gray-500",
                  "group-hover:scale-110",
                  isTransitioning ? "opacity-0 rotate-90" : "opacity-100 rotate-0"
                )}
              />
              <span
                className={cn(
                  "text-[11px] sm:text-xs font-medium",
                  "transition-all duration-300 ease-in-out",
                  isActive 
                    ? mode === 'learn' 
                      ? "text-primary" 
                      : "text-secondary"
                    : "text-gray-500",
                  isTransitioning ? "opacity-0 -translate-y-1" : "opacity-100 translate-y-0"
                )}
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNav;